import { actionTodayDateKey } from '@brightnav/care-plan';
import { ActionResource } from './http/action-api.model';
import {
  addCalendarDayKey,
  dueDateKey,
  isBeyondHorizon,
} from './due-date-filter';

/** Calendar days on the Timeline axis after the Overdue bucket. */
export const TIMELINE_DAY_COUNT = 14;

/** Timeline column kind: past-due stack, one calendar day, or 14+ days out. */
export type TimelineBarKind = 'overdue' | 'day' | 'beyond';

/** One stacked High/Normal column on the Open Actions Timeline axis. */
export interface TimelineBar {
  id: string;
  kind: TimelineBarKind;
  /** Present when `kind` is `day`. Eastern product `YYYY-MM-DD`. */
  dayKey?: string;
  label: string;
  sublabel: string;
  /** True for the calendar column that is Eastern today. */
  isToday?: boolean;
  high: number;
  normal: number;
  total: number;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** High-priority AIM actions stack in red; every other priority is Normal (blue). */
export function isHighPriority(action: ActionResource): boolean {
  return action.priorityCode === 'HIGH';
}

function stack(actions: readonly ActionResource[]): {
  high: number;
  normal: number;
  total: number;
} {
  let high = 0;
  let normal = 0;
  for (const action of actions) {
    if (isHighPriority(action)) {
      high += 1;
    } else {
      normal += 1;
    }
  }
  return { high, normal, total: high + normal };
}

function civilUtcDate(dayKey: string): Date {
  const [year, month, day] = dayKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function dayLabel(dayKey: string): string {
  const date = civilUtcDate(dayKey);
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

/**
 * Builds the Timeline axis: Overdue, Eastern today through +13 days, then
 * 3+ weeks. Empty days stay on the axis as labels with a zero stack.
 */
export function buildTimelineBars(
  actions: readonly ActionResource[],
  now = new Date(),
): TimelineBar[] {
  const todayKey = actionTodayDateKey(now);
  const overdue: ActionResource[] = [];
  const byDay = new Map<string, ActionResource[]>();
  const beyond: ActionResource[] = [];

  for (const action of actions) {
    const due = dueDateKey(action.dueDate);
    if (!due) {
      continue;
    }
    if (due < todayKey) {
      overdue.push(action);
      continue;
    }
    if (isBeyondHorizon(action.dueDate, now, TIMELINE_DAY_COUNT)) {
      beyond.push(action);
      continue;
    }
    const bucket = byDay.get(due) ?? [];
    bucket.push(action);
    byDay.set(due, bucket);
  }

  const bars: TimelineBar[] = [
    {
      id: 'overdue',
      kind: 'overdue',
      label: 'Overdue',
      sublabel: 'past due',
      ...stack(overdue),
    },
  ];

  for (let offset = 0; offset < TIMELINE_DAY_COUNT; offset += 1) {
    const key = addCalendarDayKey(todayKey, offset);
    const date = civilUtcDate(key);
    bars.push({
      id: `day:${key}`,
      kind: 'day',
      dayKey: key,
      label: dayLabel(key),
      sublabel: offset === 0 ? 'Today' : WEEKDAYS[date.getUTCDay()],
      isToday: offset === 0,
      ...stack(byDay.get(key) ?? []),
    });
  }

  bars.push({
    id: 'beyond',
    kind: 'beyond',
    label: '3+ wks',
    sublabel: '',
    ...stack(beyond),
  });

  return bars;
}

/** Y-scale for bar heights; at least 1 so an empty axis does not divide by zero. */
export function timelineMaxTotal(bars: readonly TimelineBar[]): number {
  return Math.max(1, ...bars.map((bar) => bar.total));
}
